import { useState } from 'react';
import ScreenHeader from '../../components/common/screen-header';
import PermissionGuard from '../../components/guards/PermissionGuard';
import TabBar from '../../components/common/tab-bar';
import { JOB_NAMES } from '../../constants/jobNames';
import PermissionsTab from './tabs/permissions-tab';
import PermissionSetsTab from './tabs/permission-sets-tab';
import RolesTab from './tabs/roles-tab';
import RoleBindingsTab from './tabs/role-bindings-tab';
import UserAccessTab from './tabs/user-access-tab';

/**
 * Access Control (RBAC) admin area — a single tabbed screen at /access-control.
 * Five tabs, each a DataTable list + create/edit Dialog, wired with React Query.
 *
 * The tab bar is our own rather than PrimeReact's TabView: the theme's active
 * state was too faint to read at a glance, so the selected tab carries the
 * screen's purple with an underline while the rest are muted until hovered —
 * one tab reads as selected, not five reading the same.
 */
const TABS = [
  { key: 'permissions', label: 'Permissions', icon: 'shield-user', element: <PermissionsTab /> },
  { key: 'sets', label: 'Permission Sets', icon: 'blocks', element: <PermissionSetsTab /> },
  { key: 'roles', label: 'Roles', icon: 'user-round-cog', element: <RolesTab /> },
  { key: 'bindings', label: 'Role Bindings', icon: 'link-2', element: <RoleBindingsTab /> },
  { key: 'users', label: 'User Access', icon: 'asset-user-access', element: <UserAccessTab /> },
];

const RbacDashboard = () => {
  const [activeKey, setActiveKey] = useState(TABS[0].key);
  const active = TABS.find((t) => t.key === activeKey) ?? TABS[0];

  return (
    <PermissionGuard jobName={JOB_NAMES.ROLES}>
    <div className="p-3" data-testid="SCR-AccessControl">
      <ScreenHeader
        icon="pi pi-shield"
        iconBgColor="bg-purple-600"
        title="Access Control"
        subtitle="Manage permissions, permission sets, roles, role bindings and user access"
      />

      <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <TabBar
          className="mb-4"
          tabs={TABS}
          activeKey={activeKey}
          onChange={setActiveKey}
          testIdPrefix="AccessControl.Tab"
        />
        {active.element}
      </div>
    </div>
    </PermissionGuard>
  );
};

export default RbacDashboard;
